/**
 * LicenseApiClient -- /api/v1/license
 * Gestión de licencias de Nexo Desktop: clientes y máquinas registradas.
 *
 * @module infrastructure/api/LicenseApiClient
 */

import { ApiBase } from './ApiBase.js';

const BASE = '/api/v1/license';

export const LicenseApiClient = {
  /** Lista todos los clientes registrados */
  async listClients() {
    return ApiBase.get(`${BASE}/clients`);
  },

  /**
   * Registra o actualiza un cliente (upsert por `empresa`).
   * Acepta camelCase ({ pcName, folderPath, subscriptionLimit, businessId })
   * o snake_case ({ pc_name, folder_path, subscription_limit, business_id }).
   */
  async upsertClient(data) {
    // IMPORTANTE: ASP.NET Core System.Text.Json mapea camelCase, no snake_case.
    // Las claves del body DEBEN ser camelCase para que lleguen al DTO correctamente.
    return ApiBase.post(`${BASE}/clients`, {
      empresa:           data.empresa,
      pcName:            data.pcName            ?? data.pc_name,
      folderPath:        data.folderPath        ?? data.folder_path,
      subscriptionLimit: data.subscriptionLimit ?? data.subscription_limit,
      businessId:        data.businessId        ?? data.business_id ?? null,
      notes:             data.notes,
      active:            data.active !== false,
    });
  },

  /** Lista máquinas activas que se han autenticado */
  async listMachines() {
    return ApiBase.get(`${BASE}/machines`);
  },

  /**
   * PANEL-OPS-V3-001 T4.1 — Lista instalaciones Desktop de un negocio.
   * NEEDS-API: GET /api/v1/businesses/{id}/desktop-installations
   * Fuente: nexo_private.license_machines WHERE business_id = $1
   */
  async listDesktopInstallations(businessId) {
    return ApiBase.get(`/api/v1/businesses/${encodeURIComponent(businessId)}/desktop-installations`);
  },

  /**
   * PANEL-OPS-V3-001 T4.1 — Revoca una instalacion Desktop por fingerprint.
   * NEEDS-API: POST /api/v1/businesses/{id}/desktop-installations/{fingerprint}/revoke
   */
  async revokeDesktopInstallation(businessId, fingerprint) {
    return ApiBase.post(
      `/api/v1/businesses/${encodeURIComponent(businessId)}/desktop-installations/${encodeURIComponent(fingerprint)}/revoke`,
      {}
    );
  },

  /**
   * WEB-TOKEN-PER-BUSINESS-V1-001 T3 — Setup de conexion/sincronizacion de un negocio.
   * Devuelve el web_token persistido por negocio + el flag sync_enabled (control de
   * desconexion). El token reemplaza el SYNC_API_KEY global no-persistido.
   *
   * Endpoint canonico (Argy-A T1): GET /api/v1/license/business-setup/{businessId}
   * Response esperado (camelCase System.Text.Json):
   *   { businessId, webToken, syncEnabled, tokenGeneratedAt, ... }
   *   (defensive: soporta snake_case web_token / sync_enabled por si difiere)
   */
  async getBusinessSetup(businessId) {
    return ApiBase.get(`${BASE}/business-setup/${encodeURIComponent(businessId)}`);
  },

  /**
   * WEB-TOKEN-PER-BUSINESS-V1-001 T3 — Activa/desactiva la sincronizacion del negocio.
   * Cuando sync_enabled=false el canal sync rechaza los uploads del Desktop (T2 Argy-A).
   *
   * Endpoint PROPUESTO (Argy-A confirma nombre exacto en T2):
   *   PATCH /api/v1/license/business-setup/{businessId}/sync  body { enabled: bool }
   */
  async setSyncEnabled(businessId, enabled) {
    return ApiBase.patch(
      `${BASE}/business-setup/${encodeURIComponent(businessId)}/sync`,
      { enabled: !!enabled }
    );
  },

  /**
   * WEB-TOKEN-PER-BUSINESS-V1-001 T3 — Regenera el web_token del negocio (invalida el
   * anterior). Util si el token se filtro o se rota por seguridad.
   *
   * Endpoint PROPUESTO (Argy-A confirma nombre exacto en T2):
   *   POST /api/v1/license/business-setup/{businessId}/regenerate-token
   */
  async regenerateBusinessToken(businessId) {
    return ApiBase.post(
      `${BASE}/business-setup/${encodeURIComponent(businessId)}/regenerate-token`,
      {}
    );
  },
};
